/**
 * @file axiosClient.js
 * @description custom axios client with interceptors
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 * 
 * https://medium.com/@velja/token-refresh-with-axios-interceptors-for-a-seamless-authentication-experience-854b06064bde
 * https://medium.com/@krishnanand654/jwt-token-refresh-using-axios-interceptors-03ad9fa74d77
 * https://www.xjavascript.com/blog/how-can-you-use-axios-interceptors/
 * 
 * @author don michael
 * @since Mar 18, 2026 20:18
 */
import axios from 'axios';

import { getToken } from 'src/content/Auth/authUtilities';

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

// create an axios instance
const axiosClient = axios.create({
    baseURL: baseUrl,
    headers: {
        'Content-Type': 'application/json',
    },
});

axiosClient.interceptors.request.use(request => {
    const authToken = getToken('auth');
    if (authToken) {
        request.headers.Authorization = `Bearer ${authToken}`;
    }
    return request;
}, error => {
    return Promise.reject(error);
});

/**
 * tests for a 401 or 403 to route to 
 * login
 */
axiosClient.interceptors.response.use(
    (response) => response, // Pass successful responses through
    (error) => {
        console.error('axiosClient.error', error)
        const { response, message } = error;

        // Handle network errors (no response from server)
        if (!response) {
            // alert('Network error: Please check your internet connection.');
            return Promise.reject(error);
        }

        // Handle HTTP errors
        switch (response.status) {
            case 401:
            case 403:
                // tokens expired redirect to login
                window.location.href = '/auth/signin';
                alert('Please sign in again');
                break;
            case 404:
                // alert('Server resource not found.');
                break;
            case 500:
                // alert('Server error: Please try again later.');
                break;
            default:
                alert(`Error ${response.status}: ${response.data.message || message}`);
        }

        return Promise.reject(error);
    }
);

export default axiosClient;
